<template>
  <div class="otp-window-panel" data-testid="otp-window-panel">
    <!-- Gate card: today's residual-cash gate state (OtpGateService), polled
         every 30s. Closed/none render quietly (grey); open renders green
         with the remaining balance as the headline number — that is the
         number an operator actually cares about while the gate is live. -->
    <div class="section">
      <h3 class="section-toggle" @click="showGate = !showGate">
        Gate <span class="toggle-icon">{{ showGate ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showGate" class="gate-card" :class="'gate-' + gateStatus" data-testid="otp-gate-card">
        <div class="gate-status-badge" data-testid="otp-gate-status">{{ gateStatus.toUpperCase() }}</div>
        <template v-if="gateStatus === 'open' && gate">
          <div class="gate-headline" data-testid="otp-gate-remaining">
            £{{ gate.residualRemainingGbp.toFixed(2) }} remaining
          </div>
          <div class="gate-sub">of £{{ gate.residualTotalGbp.toFixed(2) }} total &middot; {{ gate.date }}</div>
        </template>
        <template v-else-if="gateStatus === 'closed' && gate">
          <div class="gate-sub">Closed &middot; {{ gate.date }}</div>
        </template>
        <template v-else>
          <div class="gate-sub">No gate today</div>
        </template>
      </div>
    </div>

    <!-- Shadow OTP — Phase A of the 2026-09-06 shadow-promotion arc.
         Seven shadow books (CF, CF-v2, VT-m5, VT-m10, GVS-m3, Oil-CF,
         Oil-VT — CF-v2 added 2026-09-06 alongside CF's notional halving)
         run parallel to any live OTP path and deal nothing; ShadowOtpPanel
         renders the SharedPoolAllocator status card + 7-book table and
         differentiates super-user vs read-only via useAuth. Sits between
         the Gate and Registry sections so the operator sees the shadow
         surface right next to the live-day gate. -->
    <div class="section">
      <h3 class="section-toggle" @click="showShadow = !showShadow">
        Shadow OTP <span class="toggle-icon">{{ showShadow ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showShadow" data-testid="shadow-otp-section">
        <ShadowOtpPanel />
      </div>
    </div>

    <!-- Registry table: hedge is the sole active row above the
         decommission banner. Long Overlay / Long Conviction / Short
         Conviction were decommissioned 2026-09-06 (see
         docs/superpowers/plans/2026-09-06-otp-shadow-promotion-arc.md) and
         are read-only inside a closed <details> collapsible wrapped in a
         <fieldset disabled>. -->
    <div class="section">
      <h3 class="section-toggle" @click="showRegistry = !showRegistry">
        Registry <span class="toggle-icon">{{ showRegistry ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showRegistry">
        <div v-if="registryError" class="registry-error" data-testid="otp-registry-error">{{ registryError }}</div>

        <!-- Active hedge row (not decommissioned). -->
        <div class="registry-rows" v-if="hedgeRow">
          <div
            class="registry-row"
            :data-testid="`otp-registry-row-${hedgeRow.otpType}`"
          >
            <!-- Linked users final fix wave Minor 5: a viewer is read-only
                 (the server 403s registry writes) — show the state, not dead
                 controls. -->
            <span
              v-if="isViewer"
              class="registry-label"
              :data-testid="`otp-registry-readonly-${hedgeRow.otpType}`"
            >{{ otpLabel(hedgeRow.otpType) }} — {{ hedgeRow.enabled ? 'On' : 'Off' }} · target {{ targetEdits[hedgeRow.otpType] }}%</span>
            <template v-else>
            <label class="registry-toggle-label">
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  :key="`${hedgeRow.otpType}-toggle-${toggleAttempt[hedgeRow.otpType] ?? 0}`"
                  :data-testid="`otp-registry-toggle-${hedgeRow.otpType}`"
                  :checked="hedgeRow.enabled"
                  @change="toggleEnabled(hedgeRow)"
                />
                <span class="slider"></span>
              </label>
              <span class="registry-label">{{ otpLabel(hedgeRow.otpType) }}</span>
            </label>

            <input
              type="number"
              class="registry-target-input"
              :data-testid="`otp-registry-target-${hedgeRow.otpType}`"
              v-model.number="targetEdits[hedgeRow.otpType]"
              min="0"
              max="100"
              step="0.1"
            />
            <span class="registry-pct">%</span>

            <button
              type="button"
              class="btn-save-sm"
              :data-testid="`otp-registry-save-${hedgeRow.otpType}`"
              :disabled="savingType === hedgeRow.otpType"
              @click="saveTarget(hedgeRow)"
            >{{ savingType === hedgeRow.otpType ? '…' : 'Save' }}</button>
            </template>
          </div>
        </div>

        <!-- Decommission banner + collapsible over the three retired rows. -->
        <div class="decomm-banner" data-testid="otp-registry-decomm-banner" role="note">
          <strong>Long Conviction / Long Overlay / Short Conviction were decommissioned 2026-09-06.</strong>
          Hedging remains active. See
          <code>docs/superpowers/plans/2026-09-06-otp-shadow-promotion-arc.md</code>.
          New backtests targeting the three return HTTP 400.
        </div>

        <details class="decomm-collapsible" data-testid="otp-registry-decomm-collapsible">
          <summary>Show decommissioned legacy strategies (read-only)</summary>
          <fieldset
            class="decomm-fieldset"
            disabled
            data-testid="otp-registry-decomm-fieldset"
            title="Decommissioned 2026-09-06 — no writes possible; new backtests return HTTP 400."
          >
            <div class="registry-rows">
              <div
                v-for="row in decommRegistry"
                :key="row.otpType"
                class="registry-row"
                :data-testid="`otp-registry-row-${row.otpType}`"
              >
                <label class="registry-toggle-label">
                  <label class="toggle-switch" title="Decommissioned 2026-09-06 — no writes possible; new backtests return HTTP 400.">
                    <input
                      type="checkbox"
                      :data-testid="`otp-registry-toggle-${row.otpType}`"
                      :checked="row.enabled"
                      disabled
                    />
                    <span class="slider"></span>
                  </label>
                  <span class="registry-label">{{ otpLabel(row.otpType) }}</span>
                </label>

                <input
                  type="number"
                  class="registry-target-input"
                  :data-testid="`otp-registry-target-${row.otpType}`"
                  :value="targetEdits[row.otpType]"
                  min="0"
                  max="100"
                  step="0.1"
                  disabled
                />
                <span class="registry-pct">%</span>

                <button
                  type="button"
                  class="btn-save-sm"
                  :data-testid="`otp-registry-save-${row.otpType}`"
                  disabled
                  title="Decommissioned 2026-09-06 — no writes possible; new backtests return HTTP 400."
                >Save</button>
              </div>
            </div>
          </fieldset>
        </details>

        <div class="registry-sum" data-testid="otp-registry-sum">
          &Sigma; enabled targets: {{ enabledTargetSumPct.toFixed(1) }}%
        </div>
      </div>
    </div>

    <!-- Positions & history: same three data sources OtpPanel.vue uses,
         scoped to fund 'OTP' rather than BGA/JH. -->
    <div class="section">
      <h3 class="section-toggle" @click="showPositions = !showPositions">
        Positions &amp; History <span class="toggle-icon">{{ showPositions ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showPositions">
        <div class="hero-cards">
          <div class="hero-card" :style="{ borderLeftColor: (summary?.totalPnl ?? 0) >= 0 ? '#4caf50' : '#f44336' }">
            <div class="card-value" :style="{ color: (summary?.totalPnl ?? 0) >= 0 ? '#4caf50' : '#f44336' }">
              {{ (summary?.totalPnl ?? 0) >= 0 ? '+' : '' }}£{{ Math.abs(summary?.totalPnl ?? 0).toFixed(2) }}
            </div>
            <div class="card-label">Total OTP PnL</div>
            <div class="card-sub">{{ summary?.tradeCount ?? 0 }} trades</div>
          </div>

          <div class="hero-card" :style="{ borderLeftColor: '#888' }">
            <div class="card-value">{{ summary?.winRatePct ?? 0 }}%</div>
            <div class="card-label">Win Rate</div>
            <div class="card-sub">{{ summary?.wins ?? 0 }}W / {{ summary?.losses ?? 0 }}L</div>
          </div>
        </div>

        <div v-if="position" class="position-panel" data-testid="otp-active-position">
          <div class="pos-grid">
            <div class="pos-item">
              <div class="pos-label">Instrument</div>
              <div class="pos-value">{{ position.instrument }}</div>
            </div>
            <div class="pos-item">
              <div class="pos-label">Deployed</div>
              <div class="pos-value">£{{ totalDeployed.toFixed(0) }}</div>
            </div>
          </div>
        </div>

        <div class="desktop-table" v-if="trades.length">
          <table class="otp-table" data-testid="otp-trades-table">
            <thead>
              <tr>
                <th class="col-left">Date</th>
                <th class="col-left">Type</th>
                <th class="col-left">Inst</th>
                <th class="col-right">Entry</th>
                <th class="col-right">Exit</th>
                <th class="col-right">PnL</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(t, i) in trades" :key="t.hedgeTradeId" :class="{ 'row-alt': i % 2 === 1 }">
                <td class="col-left">{{ formatDate(t.date) }}</td>
                <td class="col-left">{{ otpLabel(t.otpType) }}</td>
                <td class="col-left ticker">{{ t.instrument }}</td>
                <td class="col-right">{{ formatPence(t.entryPrice) }}</td>
                <td class="col-right">{{ formatPence(t.exitPrice) }}</td>
                <td class="col-right" :class="pnlClass(t.pnl)">
                  {{ t.pnl != null ? `${t.pnl >= 0 ? '+' : '-'}£${Math.abs(t.pnl).toFixed(2)}` : '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mobile-cards" v-if="trades.length">
          <div
            v-for="t in trades"
            :key="'m' + t.hedgeTradeId"
            class="trade-card"
            :style="{ borderLeftColor: (t.pnl ?? 0) >= 0 ? '#4caf50' : '#f44336' }"
          >
            <div class="trade-card-header">
              <strong>{{ t.instrument }}</strong>
              <span class="otp-type-badge">{{ otpLabel(t.otpType) }}</span>
              <span style="margin-left:auto;font-size:0.75rem;color:#888;">{{ formatDate(t.date) }}</span>
            </div>
            <div class="trade-card-metrics">
              PnL: <span :class="pnlClass(t.pnl)">{{ t.pnl != null ? `${t.pnl >= 0 ? '+' : '-'}£${Math.abs(t.pnl).toFixed(2)}` : '—' }}</span>
            </div>
          </div>
        </div>

        <div v-if="!trades.length" class="no-trades">No completed OTP trades yet.</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// otp-standalone Plan C, Task 3: the OTP window — a reduced-scope panel
// that lives on the otp portfolio's OTP tab (Dashboard.vue), replacing the
// per-fund OtpPanel/GoldOtpPanel mounts. Consumes: gate visibility
// (api.otpGate, Task 2), the Plan B component registry (api.otpRegistry /
// otpRegistryUpdate, this task), and the existing OTP summary/position/
// trades endpoints scoped to fund 'OTP'.
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type OtpGateView, type OtpRegistryRow } from '../api/client'
import { useAuth } from '../composables/useAuth'
import ShadowOtpPanel from './ShadowOtpPanel.vue'

const { isViewer } = useAuth()

const showGate = ref(true)
const showShadow = ref(true)
const showRegistry = ref(true)
const showPositions = ref(true)

// ── Gate card ──────────────────────────────────────────────────────────
const gate = ref<OtpGateView | null>(null)

// The shared request<T>() helper returns `undefined` (not `null`) for a
// 204 response — guard falsy here rather than `=== null`.
const gateStatus = computed<'open' | 'closed' | 'none'>(() => {
  if (!gate.value) return 'none'
  return gate.value.status === 'open' ? 'open' : 'closed'
})

async function loadGate() {
  try { gate.value = (await api.otpGate()) ?? null } catch { /* keep previous reading */ }
}

// ── Registry table ─────────────────────────────────────────────────────
// hedge is the sole active row above the decommission banner (2026-09-06
// arc); overlay / long_conv / short_conv are decommissioned — read-only
// inside a disabled fieldset, but still surfaced so the operator can see
// the historic config.
const REGISTRY_LABELS: Record<string, string> = {
  hedge: 'Hedging',
  overlay: 'Long Overlay',
  long_conv: 'Long Conviction',
  short_conv: 'Short Conviction',
}

const DECOMMISSIONED_OTP_TYPES = new Set(['overlay', 'long_conv', 'short_conv'])

function otpLabel(t: string): string {
  return REGISTRY_LABELS[t] ?? t
}

const registry = ref<OtpRegistryRow[]>([])
const registryError = ref<string | null>(null)
const savingType = ref<string | null>(null)
// Locally-edited percent values (0-100), keyed by otpType — the wire value
// (targetPct) is a 0-1 fraction; /100 on save per the task brief.
const targetEdits = ref<Record<string, number>>({})

const hedgeRow = computed<OtpRegistryRow | null>(
  () => registry.value.find(r => r.otpType === 'hedge') ?? null,
)
const decommRegistry = computed(() =>
  registry.value.filter(r => DECOMMISSIONED_OTP_TYPES.has(r.otpType)),
)
// The sum line reflects everything the registry still tracks (hedge +
// decommissioned rows read-only for reference).
const enabledTargetSumPct = computed(() =>
  registry.value
    .filter(r => r.enabled)
    .reduce((sum, r) => sum + r.targetPct * 100, 0))

async function loadRegistry() {
  try {
    const rows = await api.otpRegistry()
    registry.value = rows
    for (const r of rows) {
      targetEdits.value[r.otpType] = Math.round(r.targetPct * 1000) / 10
    }
  } catch { /* keep previous rows */ }
}

// task-3-review.md finding 2 (Major): the checkbox is bound one-way
// (:checked="row.enabled", no v-model) so a native click flips the DOM's
// `checked` property BEFORE this handler even runs. On a rejected PUT,
// row.enabled never actually changes (no loadRegistry() re-fetch on the
// catch path), so Vue's DOM patcher — which only writes `checked` when
// the bound value differs from last render — has nothing to diff and
// silently leaves the DOM showing whatever the user's own click set,
// contradicting the still-unpersisted server state. toggleAttempt (bumped
// unconditionally in `finally`, success or failure) forces the checkbox's
// `:key` to change, so Vue recreates the element fresh from the current
// row.enabled instead of diffing against a possibly-identical previous
// value — this is what actually snaps a rejected toggle back to server
// truth. On success, loadRegistry() already gives a genuinely different
// value so the patch (and the recreate) land on the same visual result.
const toggleAttempt = ref<Record<string, number>>({})

async function toggleEnabled(row: OtpRegistryRow) {
  registryError.value = null
  savingType.value = row.otpType
  try {
    await api.otpRegistryUpdate(row.otpType, { enabled: !row.enabled })
    await loadRegistry()
  } catch (e: any) {
    registryError.value = e.message ?? 'Failed to update OTP registry'
  } finally {
    savingType.value = null
    toggleAttempt.value[row.otpType] = (toggleAttempt.value[row.otpType] ?? 0) + 1
  }
}

async function saveTarget(row: OtpRegistryRow) {
  registryError.value = null
  const pct = targetEdits.value[row.otpType]
  if (pct == null || Number.isNaN(pct)) return
  savingType.value = row.otpType
  try {
    await api.otpRegistryUpdate(row.otpType, { targetPct: pct / 100 })
    await loadRegistry()
  } catch (e: any) {
    registryError.value = e.message ?? 'Failed to update OTP registry'
  } finally {
    savingType.value = null
  }
}

// ── Positions & history ─────────────────────────────────────────────────
const summary = ref<any>(null)
const position = ref<any>(null)
const trades = ref<any[]>([])

const totalDeployed = computed(() => {
  if (!position.value?.accounts) return 0
  return position.value.accounts.reduce((sum: number, a: any) => sum + (a.amount ?? 0), 0)
})

function formatPence(p: number | null | undefined): string {
  if (p == null) return '—'
  return p.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'p'
}

function formatDate(d: string): string {
  if (!d) return '—'
  const dt = new Date(d + 'T00:00:00')
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

function pnlClass(pnl: number | null | undefined): string {
  if (pnl == null) return ''
  return pnl >= 0 ? 'pnl-pos' : 'pnl-neg'
}

async function loadPositions() {
  try { summary.value = await api.otpSummary('OTP') } catch { /* no summary data */ }
  try { position.value = await api.otpPosition('OTP') } catch { /* no active position */ }
  try { trades.value = (await api.otpTrades(20, 'OTP')) ?? [] } catch { /* no trades */ }
}

let gatePoll: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  loadGate()
  loadRegistry()
  loadPositions()
  gatePoll = setInterval(loadGate, 30000)
})
onUnmounted(() => { if (gatePoll) clearInterval(gatePoll) })
</script>

<style scoped>
.otp-window-panel { padding: 0.5rem 0; }

.section { margin-bottom: 1rem; }
.section-toggle {
  cursor: pointer; user-select: none; font-size: 0.9rem; color: #aaa;
  border-bottom: 1px solid #333; padding-bottom: 0.3rem; margin: 0;
}
.section-toggle:hover { color: #e0e0e0; }
.toggle-icon { font-size: 0.8rem; margin-left: 0.5rem; }

/* Gate card */
.gate-card {
  background: #1a1a1a; border: 1px solid #333; border-left: 3px solid #555;
  border-radius: 6px; padding: 0.75rem; margin-top: 0.5rem;
}
.gate-card.gate-open { border-left-color: #4caf50; background: #16241a; }
.gate-card.gate-closed, .gate-card.gate-none { border-left-color: #555; color: #888; }
.gate-status-badge {
  display: inline-block; font-size: 0.9rem; font-weight: 700; letter-spacing: 0.08em;
  color: #ccc;
}
.gate-card.gate-open .gate-status-badge { color: #4caf50; }
.gate-headline {
  font-family: 'JetBrains Mono', monospace; font-size: 1.4rem; font-weight: 600;
  color: #4caf50; margin-top: 0.3rem;
}
.gate-sub { font-size: 0.8rem; color: #888; margin-top: 0.2rem; }

/* Registry table */
.registry-error {
  padding: 8px 10px; margin-top: 0.5rem; background: #3a1b1b; border: 1px solid #f44336;
  border-radius: 4px; color: #f44336; font-size: 0.8rem;
}
.registry-rows { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 0.5rem; }
.registry-row {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem;
  background: #1a1a1a; border: 1px solid #333; border-radius: 6px; padding: 0.6rem;
}
.registry-toggle-label { display: flex; align-items: center; gap: 0.5rem; min-width: 140px; }
.registry-label { font-weight: 600; color: #ccc; font-size: 0.85rem; }
.registry-target-input {
  width: 70px; padding: 6px 8px; background: #222; color: #ccc;
  border: 1px solid #444; border-radius: 4px; font-size: 13px; min-height: 36px;
}
.registry-pct { color: #888; font-size: 0.8rem; }
.btn-save-sm {
  margin-left: auto; padding: 6px 12px; background: transparent; color: #58a6ff;
  border: 1px solid #58a6ff; border-radius: 4px; font-size: 0.78rem; cursor: pointer; min-height: 36px;
}
.btn-save-sm:disabled { opacity: 0.4; cursor: not-allowed; }
.registry-sum { margin-top: 0.5rem; font-size: 0.85rem; color: #ccc; }

/* Decommissioned strategies banner + collapsible — Phase B (2026-09-06). */
.decomm-banner {
  background: #2a2210; border: 1px solid #6b5a1f; border-radius: 4px;
  padding: 0.6rem 0.75rem; margin: 0.75rem 0 0.5rem;
  font-size: 0.8rem; color: #e0c86b; line-height: 1.45;
}
.decomm-banner code {
  background: #1a1a1a; padding: 1px 4px; border-radius: 3px;
  font-size: 0.75rem; color: #eec;
}
.decomm-collapsible {
  background: #14181c; border: 1px solid #2a2f36; border-radius: 4px;
  padding: 0.4rem 0.6rem; margin: 0 0 0.5rem;
}
.decomm-collapsible > summary {
  cursor: pointer; user-select: none; font-size: 0.8rem;
  color: #b8a768; letter-spacing: 0.5px; padding: 0.15rem 0;
}
.decomm-collapsible[open] > summary { border-bottom: 1px solid #333; margin-bottom: 0.5rem; padding-bottom: 0.35rem; }
.decomm-fieldset { border: 1px dashed #3a3a3a; opacity: 0.7; padding: 0.5rem; margin: 0; }
.decomm-fieldset[disabled] { pointer-events: none; }

.toggle-switch { position: relative; display: inline-block; width: 36px; height: 20px; }
.toggle-switch input { opacity: 0; width: 0; height: 0; }
.toggle-switch .slider {
  position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
  background: #333; border-radius: 20px; transition: 0.2s;
}
.toggle-switch .slider:before {
  content: ""; position: absolute; height: 14px; width: 14px; left: 3px; bottom: 3px;
  background: #999; border-radius: 50%; transition: 0.2s;
}
.toggle-switch input:checked + .slider { background: #1b3a1b; }
.toggle-switch input:checked + .slider:before { background: #4caf50; transform: translateX(16px); }

/* Positions & history — same visual language as OtpPanel.vue */
.hero-cards { display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1rem; margin-top: 0.5rem; }
@media (min-width: 769px) { .hero-cards { flex-direction: row; } .hero-card { flex: 1; } }
.hero-card {
  background: #1a1a1a; border: 1px solid #333; border-left: 3px solid #888;
  border-radius: 6px; padding: 0.75rem;
}
.card-value { font-family: 'JetBrains Mono', monospace; font-size: 1.5rem; font-weight: 600; }
.card-label { font-size: 0.8rem; color: #aaa; margin-top: 0.25rem; }
.card-sub { font-size: 0.75rem; color: #888; margin-top: 0.15rem; }

.position-panel {
  background: #1a2a1a; border: 1px solid #2a4a2a; border-radius: 6px;
  padding: 0.75rem; margin-bottom: 1rem;
}
.pos-grid { display: flex; flex-wrap: wrap; gap: 1.5rem; }
.pos-label { font-size: 0.7rem; color: #888; text-transform: uppercase; letter-spacing: 0.03em; }
.pos-value { font-family: 'JetBrains Mono', monospace; font-size: 1rem; color: #e0e0e0; }

.no-trades { color: #666; font-size: 0.85rem; padding: 0.5rem 0; }

.desktop-table { display: none; overflow-x: auto; margin-top: 0.5rem; }
@media (min-width: 769px) { .desktop-table { display: block; } .mobile-cards { display: none !important; } }

.otp-table {
  width: 100%; border-collapse: collapse;
  font-family: 'JetBrains Mono', monospace; font-size: 0.85rem; line-height: 1.7;
}
.otp-table thead tr { background: #111; border-bottom: 1px solid #444; }
.otp-table th { padding: 0.45rem 0.7rem; font-weight: 600; color: #888; text-transform: uppercase; font-size: 0.75rem; white-space: nowrap; }
.otp-table td { padding: 0.35rem 0.7rem; white-space: nowrap; color: #ccc; }
.otp-table tbody tr { background: #1a1a1a; }
.otp-table tbody tr.row-alt { background: #1f1f1f; }
.otp-table tbody tr:hover { background: #252525; }
.col-left { text-align: left; }
.col-right { text-align: right; }
.ticker { color: #ddd; font-weight: 600; }
.pnl-pos { color: #4caf50; font-weight: 600; }
.pnl-neg { color: #f44336; font-weight: 600; }

.mobile-cards { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 0.5rem; }
.trade-card {
  background: #1a1a1a; border: 1px solid #333; border-left: 3px solid #888;
  border-radius: 6px; padding: 0.6rem;
}
.trade-card-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.3rem; }
.trade-card-metrics { font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; color: #ccc; }
.otp-type-badge { font-size: 0.7rem; color: #6e7681; }

@media (max-width: 600px) {
  .registry-row { flex-direction: column; align-items: stretch; }
  .btn-save-sm { margin-left: 0; }
}
</style>
